import type { ICellRendererParams } from 'ag-grid-enterprise';
import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { type ProjectLogEntry, timeEntryService } from '../../services/timeEntryService';
import { colors } from '../../theme/colors';
import { ProjectHoursDialog } from './ProjectHoursDialog';

interface ActionCellRendererProps extends ICellRendererParams {
  selectedDate: Date;
  onEdit: (entry: ProjectLogEntry) => void;
  onDelete: (id: string) => void;
  disabled?: boolean;
  actualHours?: number;
  plannedHours?: number;
  projectLoggedHours?: ProjectLogEntry[];
}

export const ActionCellRenderer: React.FC<ActionCellRendererProps> = ({
  selectedDate,
  data,
  onEdit,
  onDelete,
  disabled = false,
  actualHours,
  plannedHours,
  projectLoggedHours,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEdit = () => {
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (isDeleting) return; // Prevent multiple clicks

    try {
      setIsDeleting(true);
      await timeEntryService.deleteTimeEntry(data.id.toString());
      onDelete(data.id);
    } catch (error) {
      // Error deleting time entry
      alert('Failed to delete time entry. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = (entry: ProjectLogEntry) => {
    onEdit(entry);
    setIsDialogOpen(false);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const buttonStyle = {
    backgroundColor: colors.accent.blue,
    color: colors.neutral.white,
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `0 2px 4px ${colors.accent.blue}30`,
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    minWidth: '40px',
    justifyContent: 'center',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  };

  const deleteButtonStyle = {
    ...buttonStyle,
    backgroundColor: colors.accent.red,
    boxShadow: `0 2px 4px ${colors.accent.red}30`,
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>, isDelete = false) => {
    const baseColor = isDelete ? colors.accent.red : colors.accent.blue;
    e.currentTarget.style.backgroundColor = baseColor + 'dd';
    e.currentTarget.style.transform = 'translateY(-2px)';
    e.currentTarget.style.boxShadow = `0 4px 12px ${baseColor}40`;
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>, isDelete = false) => {
    const baseColor = isDelete ? colors.accent.red : colors.accent.blue;
    e.currentTarget.style.backgroundColor = baseColor;
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = `0 2px 4px ${baseColor}30`;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>, isDelete = false) => {
    const baseColor = isDelete ? colors.accent.red : colors.accent.blue;
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = `0 1px 2px ${baseColor}50`;
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>, isDelete = false) => {
    const baseColor = isDelete ? colors.accent.red : colors.accent.blue;
    e.currentTarget.style.transform = 'translateY(-2px)';
    e.currentTarget.style.boxShadow = `0 4px 12px ${baseColor}40`;
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0.2rem',
        }}
      >
        <button
          onClick={handleEdit}
          disabled={disabled}
          style={{
            ...buttonStyle,
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? colors.neutral.grey[400] : buttonStyle.backgroundColor,
          }}
          onMouseEnter={(e) => !disabled && handleMouseEnter(e, false)}
          onMouseLeave={(e) => !disabled && handleMouseLeave(e, false)}
          onMouseDown={(e) => !disabled && handleMouseDown(e, false)}
          onMouseUp={(e) => !disabled && handleMouseUp(e, false)}
          title="Edit row"
        >
          <EditIcon />
          Edit
        </button>

        <button
          onClick={handleDelete}
          disabled={disabled || isDeleting}
          style={{
            ...deleteButtonStyle,
            opacity: isDeleting || disabled ? 0.6 : 1,
            cursor: isDeleting || disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? colors.neutral.grey[400] : deleteButtonStyle.backgroundColor,
          }}
          onMouseEnter={(e) => !isDeleting && !disabled && handleMouseEnter(e, true)}
          onMouseLeave={(e) => !isDeleting && !disabled && handleMouseLeave(e, true)}
          onMouseDown={(e) => !isDeleting && !disabled && handleMouseDown(e, true)}
          onMouseUp={(e) => !isDeleting && !disabled && handleMouseUp(e, true)}
          title={isDeleting ? 'Deleting...' : 'Delete row'}
        >
          <TrashIcon />
          {isDeleting ? 'Deleting...' : ''}
        </button>
      </div>

      <ProjectHoursDialog
        selectedDate={selectedDate}
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
        entry={data}
        actualHours={actualHours}
        plannedHours={plannedHours}
        isEdit={true}
        projectLoggedHours={projectLoggedHours || []}
      />
    </>
  );
};
